import React, { useState } from "react";
import {
  Table,
  Tag,
  Button,
  Space,
  InputNumber,
  Typography,
  Tooltip,
  Form,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { CheckOutlined, CloseOutlined, EditOutlined, LockOutlined, NumberOutlined } from "@ant-design/icons";
import { ParticipantWithBib } from "../types/bib.types";
import { bibStatusColor } from "../utils/bib.utils";


const { Text } = Typography;

interface BibTableProps {
  participants?: ParticipantWithBib[];
  registrations?: ParticipantWithBib[];
  loading?: boolean;
  mode?: "view" | "assign";
  onManualAssign?: (id: string, bib: number) => void;
  // Server-side pagination (used when onPageChange is provided)
  total?: number;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  // BIB validation
  savedRange?: { start: number; end: number; prefix?: string };
  existingBibs?: number[];
}

const BibTable: React.FC<BibTableProps> = ({
  participants,
  registrations,
  loading,
  mode = "view",
  onManualAssign,
  total,
  currentPage,
  pageSize,
  onPageChange,
  savedRange,
  existingBibs = [],
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBib, setEditBib] = useState<number | null>(null);
  const [bibError, setBibError] = useState<string | null>(null);

  const data = participants || registrations || [];

  const validateBib = (value: number | null, recordId: string): string | null => {
    if (!value) return null;
    // Check range constraint
    if (savedRange && (value < savedRange.start || value > savedRange.end)) {
      return `Must be ${savedRange.start}–${savedRange.end}`;
    }
    // Check duplicate (ignore the current record's own BIB)
    // Use Number() to avoid type mismatch between string and number
    const currentBib = Number(data.find((r) => r.id === recordId)?.bib_number);
    const numValue = Number(value);
    if (existingBibs.some((b) => Number(b) === numValue) && numValue !== currentBib) {
      return "BIB already in use";
    }
    return null;
  };

  const handleEditStart = (record: ParticipantWithBib) => {
    const currentBib = record.bib_number ?? null;
    setEditingId(record.id);
    setEditBib(currentBib);
    // Validate immediately so out-of-range existing BIBs show an error on open
    setBibError(validateBib(currentBib, record.id));
  };

  const handleBibChange = (value: number | null, recordId: string) => {
    setEditBib(value);
    setBibError(validateBib(value, recordId));
  };

  const handleConfirmAssign = (record: ParticipantWithBib) => {
    const err = validateBib(editBib, record.id);
    if (err) { setBibError(err); return; }
    if (editBib) {
      onManualAssign?.(record.id, editBib);
      setEditingId(null);
      setEditBib(null);
      setBibError(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditBib(null);
    setBibError(null);
  };

  const columns: ColumnsType<ParticipantWithBib> = [
    {
      title: "#",
      width: 50,
      render: (_, __, i) => <Text type="secondary">{i + 1}</Text>,
    },
    {
      title: "Participant",
      key: "name",
      render: (_, r) => (
        <div>
          <Text strong>{r.first_name} {r.last_name}</Text>
          <br />
          <Text type="secondary" className="text-xs">{r.email}</Text>
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "ticket_name",
      key: "ticket_name",
      render: (v) => <Tag>{v || "—"}</Tag>,
    },
    ...(participants?.some((p) => p.distance) || registrations?.some((p) => p.distance)
      ? [
          {
            title: "Distance",
            dataIndex: "distance" as keyof ParticipantWithBib,
            key: "distance",
            render: (v: any) => v ? <Tag color="cyan">{v}</Tag> : "—",
          },
        ]
      : []),
    {
      title: "BIB Number",
      dataIndex: "bib_number",
      key: "bib_number",
      render: (bib, record) => {
        if (editingId === record.id) {
          return (
            <div style={{ minWidth: 180 }}>
              {savedRange && (
                <Text
                  type="secondary"
                  style={{ fontSize: 11, display: "block", marginBottom: 4 }}
                >
                  Allowed range: {savedRange.start}–{savedRange.end}
                </Text>
              )}
              <Form.Item
                validateStatus={bibError ? "error" : ""}
                help={bibError}
                style={{ marginBottom: 0 }}
              >
                <Space.Compact>
                  <InputNumber
                    min={savedRange?.start ?? 1}
                    max={savedRange?.end ?? undefined}
                    value={editBib ?? undefined}
                    onChange={(v) => {
                      // Hard-block any value beyond the upper limit
                      if (
                        v !== null &&
                        savedRange &&
                        v > savedRange.end
                      ) {
                        return;
                      }
                      handleBibChange(v, record.id);
                    }}
                    onBlur={() => setBibError(validateBib(editBib, record.id))}
                    className="w-28"
                    autoFocus
                    status={bibError ? "error" : undefined}
                    placeholder={
                      savedRange
                        ? `${savedRange.start}–${savedRange.end}`
                        : "BIB #"
                    }
                    onPressEnter={() => handleConfirmAssign(record)}
                  />
                  <Tooltip
                    title={
                      bibError
                        ? bibError
                        : !editBib
                          ? "Enter a BIB number"
                          : "Confirm assignment"
                    }
                  >
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      disabled={!!bibError || !editBib}
                      onClick={() => handleConfirmAssign(record)}
                    />
                  </Tooltip>
                  <Button
                    icon={<CloseOutlined />}
                    onClick={handleCancelEdit}
                  />
                </Space.Compact>
              </Form.Item>
            </div>
          );
        }
        const isAuthorized = record.bib_status === "authorized";
        const bibLabel = savedRange?.prefix ? `${savedRange.prefix}-${bib}` : bib;
        return bib ? (
          <Space>
            <Text strong style={{ color: isAuthorized ? "#16a34a" : "#1890ff", fontSize: 16 }}>
              {bibLabel}
            </Text>
            {isAuthorized && (
              <Tooltip title="BIB published — locked and sent to participant">
                <LockOutlined style={{ color: "#16a34a", fontSize: 12 }} />
              </Tooltip>
            )}
            {mode === "assign" && !isAuthorized && (
              <Button
                type="text"
                size="small"
                icon={<EditOutlined className="text-xs" />}
                onClick={() => handleEditStart(record)}
              />
            )}
          </Space>
        ) : mode === "assign" ? (
          <Button
            type="default"
            size="small"
            icon={<NumberOutlined />}
            style={{ borderColor: "#d97706", color: "#d97706" }}
            onClick={() => handleEditStart(record)}
          >
            Assign BIB
          </Button>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "bib_status",
      key: "bib_status",
      render: (status) => {
        const label =
          status === "authorized"
            ? "Published"
            : status
              ? status.charAt(0).toUpperCase() + status.slice(1)
              : "Pending";
        return <Tag color={bibStatusColor(status)}>{label}</Tag>;
      },
    },
  ];

  const paginationConfig = onPageChange
    ? {
        current: currentPage ?? 1,
        pageSize: pageSize ?? 10,
        total: total ?? data.length,
        onChange: onPageChange,
        showSizeChanger: false,
        showTotal: (t: number) => `Total ${t} participants`,
      }
    : { pageSize: 15, showSizeChanger: true };

  return (
    <Table
      columns={columns}
      dataSource={data}
      loading={loading}
      rowKey="id"
      pagination={paginationConfig}
      size="small"
      scroll={{ x: 800 }}
      onRow={(record) => ({
        style:
          mode === "assign" && !record.bib_number
            ? { backgroundColor: "#fffbeb" }
            : undefined,
      })}
    />
  );
};

export default BibTable;
